import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { getPrayers, deletePrayer, createPrayer, updatePrayer } from "../../../utils/api";

const columns = [
  { key: "title", label: "Title" },
  { key: "type", label: "Type" },
  { key: "category", label: "Category" },
  { 
    key: "createdOn", 
    label: "Created On",
    render: (value) => value ? new Date(value).toLocaleDateString() : "-"
  },
];

const filters = [
  {
    key: "type",
    label: "Filter by Type",
    type: "select",
    options: [
      { value: "Mantra", label: "Mantra" },
      { value: "Stotra", label: "Stotra" },
      { value: "Aarti", label: "Aarti" },
      { value: "Chalisa", label: "Chalisa" },
      { value: "Bhajan", label: "Bhajan" },
    ]
  },
  {
    key: "category",
    label: "Filter by Category",
    type: "select",
    options: [
      { value: "Morning", label: "Morning" },
      { value: "Evening", label: "Evening" },
      { value: "Special", label: "Special" },
      { value: "Festival", label: "Festival" },
    ]
  },
];

const initialFormData = {
  title: "",
  description: "",
  transcript: "",
  type: "Mantra",
  category: "Morning",
  audioBook: "",
  videoBook: "",
  image: ""
};

export default function Prayers() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedPrayer, setSelectedPrayer] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [audioFile, setAudioFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAdd = () => {
    setFormData(initialFormData);
    setAudioFile(null);
    setVideoFile(null);
    setIsEditMode(false);
    setSelectedPrayer(null);
    setModalOpen(true);
  };

  const handleEdit = (prayer) => {
    setSelectedPrayer(prayer);
    setFormData({
      title: prayer.title || "",
      description: prayer.description || "",
      transcript: prayer.transcript || "",
      type: prayer.type || "Mantra",
      category: prayer.category || "Morning",
      audioBook: prayer.audioBook || "",
      videoBook: prayer.videoBook || "",
      image: prayer.image || ""
    });
    setAudioFile(null);
    setVideoFile(null);
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (prayer) => {
    handleEdit(prayer);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const maxSize = 500;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5);
          
          setFormData(prev => ({ ...prev, image: compressedBase64 }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAudioFileChange = (e) => {
    const file = e.target.files[0];
    setAudioFile(file || null);
    if (file) {
      setFormData((prev) => ({ ...prev, audioBook: "" }));
    }
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    setVideoFile(file || null);
    if (file) {
      setFormData((prev) => ({ ...prev, videoBook: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // If user selected media files, we need to submit as multipart/form-data
    const useFormData = audioFile || videoFile;
    let payload;

    if (useFormData) {
      const form = new FormData();
      form.append("title", formData.title);
      form.append("description", formData.description);
      form.append("transcript", formData.transcript);
      form.append("type", formData.type);
      form.append("category", formData.category);
      form.append("image", formData.image);

      if (audioFile) {
        form.append("audioBook", audioFile);
      } else if (formData.audioBook) {
        form.append("audioBook", formData.audioBook);
      }

      if (videoFile) {
        form.append("videoBook", videoFile);
      } else if (formData.videoBook) {
        form.append("videoBook", formData.videoBook);
      }

      payload = form;
    } else {
      payload = {
        title: formData.title,
        description: formData.description,
        transcript: formData.transcript,
        type: formData.type,
        category: formData.category,
        audioBook: formData.audioBook,
        videoBook: formData.videoBook,
        image: formData.image,
      };
    }

    try {
      if (isEditMode && selectedPrayer) {
        await updatePrayer(selectedPrayer._id, payload);
        alert("Prayer updated successfully!");
      } else {
        await createPrayer(payload);
        alert("Prayer created successfully!");
      }
      setModalOpen(false);
    } catch (error) {
      alert(error.message || "Failed to save prayer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Prayers"
          fetchData={getPrayers}
          deleteData={deletePrayer}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search prayers..."
        />
      </main>

      {/* Create/Edit Prayer Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Prayer" : "Add New Prayer"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                required
                placeholder="Enter prayer title"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Type *</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleFormChange}
                  required
                >
                  <option value="Mantra">Mantra</option>
                  <option value="Stotra">Stotra</option>
                  <option value="Aarti">Aarti</option>
                  <option value="Chalisa">Chalisa</option>
                  <option value="Bhajan">Bhajan</option>
                </select>
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleFormChange}
                  required
                >
                  <option value="Morning">Morning</option>
                  <option value="Evening">Evening</option>
                  <option value="Special">Special</option>
                  <option value="Festival">Festival</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                rows="3"
                placeholder="Enter description"
              />
            </div>
            <div className="form-group">
              <label>Transcript</label>
              <textarea
                name="transcript"
                value={formData.transcript}
                onChange={handleFormChange}
                rows="5"
                placeholder="Enter prayer text/transcript"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Media</h3>
            <div className="form-group">
              <label>Audio Book URL</label>
              <input
                type="url"
                name="audioBook"
                value={formData.audioBook}
                onChange={handleFormChange}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <label>Or Upload Audio File</label>
              <input
                type="file"
                accept="audio/*"
                onChange={handleAudioFileChange}
                className="file-input"
              />
              {audioFile && (
                <div className="file-preview">
                  <span>{audioFile.name}</span>
                  <button type="button" onClick={() => setAudioFile(null)}>
                    Remove
                  </button>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Video Book URL</label>
              <input
                type="url"
                name="videoBook"
                value={formData.videoBook}
                onChange={handleFormChange}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <label>Or Upload Video File</label>
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoFileChange}
                className="file-input"
              />
              {videoFile && (
                <div className="file-preview">
                  <span>{videoFile.name}</span>
                  <button type="button" onClick={() => setVideoFile(null)}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>Image</h3>
            <div className="form-group">
              <label>Prayer Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {formData.image && (
                <div className="image-preview">
                  <img src={formData.image} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px' }} />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, image: "" }))}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : (isEditMode ? "Update Prayer" : "Create Prayer")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
